/**
 * "This session is over — the user has to sign in again."
 *
 * Deciding that means knowing two things: the SDK's own error constants, and which of the server's
 * 401 bodies are recoverable. Both are SDK knowledge, so both live here rather than being restated
 * by each host app.
 *
 * They were restated, and both copies were wrong. The Developer Portal's copy of the SDK error
 * string read "Bayun Error Reauthentication Needed" against the SDK's
 * "BayunErrorReauthenticationNeeded", so its expiry handling never once ran. AdminComponent's copy
 * of the refresh condition matched only "Token not found", so an ordinary expired token — the common
 * case — skipped the refresh and failed the request instead. Neither was detectable by reading the
 * app alone.
 *
 * Two questions, deliberately separate:
 *
 *  - isRefreshableAuthError: the access token is stale but the refresh cookie may still be good.
 *    Retry via /auth/refreshToken. Recoverable, invisible to the user.
 *  - isReauthenticationNeeded: nothing local or remote can revive this session. Terminal.
 */

import BayunError from "../constants/BayunError";
import LmsErrorConstants from "../constants/LmsErrorConstants";
import { getSessionRecord } from "./SessionRegistry";

/**
 * The server sets this errorType on every session failure, whatever the cause, so it confirms the
 * shape of the body rather than narrowing the cause. The message is the discriminator.
 */
const ACCESS_DENIED = "ACCESS_DENIED";

/**
 * SDK errors arrive as plain strings (BayunError constants), as Errors, and as axios rejections
 * carrying the server's text. A test that read only one of the three is how both app-side copies of
 * this logic went wrong.
 */
const messagesIn = (error) => {
  if (typeof error === "string") {
    return [error.trim()];
  }
  if (!error) {
    return [];
  }
  const body = error.response?.data;
  return [body?.errorMessage, body?.message, error.message]
    .filter((value) => typeof value === "string")
    .map((value) => value.trim())
    .filter(Boolean);
};

const says = (error, ...expected) =>
  messagesIn(error).some((message) => expected.includes(message));

const statusOf = (error) => error?.response?.status;

const errorTypeOf = (error) => error?.response?.data?.errorType;

/**
 * Whether a failed request should be retried after refreshing the access token.
 *
 * Both messages mean "this access token is no longer usable, try the refresh cookie". Matching only
 * the first signed users out while their refresh token was still valid.
 */
export const isRefreshableAuthError = (error) =>
  statusOf(error) === 401 &&
  errorTypeOf(error) === ACCESS_DENIED &&
  says(error, LmsErrorConstants.ERROR_TOKEN_NOT_FOUND, LmsErrorConstants.EXPIRED_TOKEN);

/**
 * Whether the user has to sign in again.
 *
 * Covers both halves of "session gone": the SDK finding no decryptable state for the session, and
 * the server resolving the session but refusing it as no longer logged in. A failed token refresh
 * also means this, but the caller knows that without inspecting an error — see
 * reportReauthenticationNeeded.
 */
export const isReauthenticationNeeded = (error) =>
  says(error, BayunError.ERROR_REAUTHENTICATION_NEEDED) ||
  (errorTypeOf(error) === ACCESS_DENIED && says(error, LmsErrorConstants.RE_AUTHENTICATE));

const handlers = new Set();

/**
 * Registers a handler to run when a session turns out to be over.
 *
 * Registered once at startup rather than passed to each call, because the discovery happens deep in
 * a storage read or an HTTP interceptor, far from whatever the user was doing. Hosts use it to clear
 * their auth state and show a sign-in screen.
 *
 * @param {(detail: { sessionId: String }) => void} handler
 * @returns {Function} unsubscribe
 */
export const onReauthenticationNeeded = (handler) => {
  handlers.add(handler);
  return () => handlers.delete(handler);
};

let reporting = false;

/**
 * Tells the host that a session is over.
 *
 * Debounced across a burst: one page load can make a dozen storage reads that all fail the same
 * way, and the host should see one sign-out, not a dozen. The flag clears on the next microtask, so
 * a later genuine expiry still reports.
 *
 * Only reports for a session that had finished signing in. Reads for a session that never got that
 * far — a login in progress, a probe before any account exists — fail this way too, and reporting
 * those would sign the user out mid-login.
 *
 * @param {String} sessionId the session that turned out to be over.
 */
export const reportReauthenticationNeeded = (sessionId) => {
  if (!sessionId || reporting) {
    return;
  }
  let record;
  try {
    record = getSessionRecord(sessionId);
  } catch (error) {
    return;
  }
  if (!record || !record.signedInAt) {
    return;
  }
  reporting = true;
  Promise.resolve().then(() => {
    reporting = false;
  });
  handlers.forEach((handler) => {
    try {
      handler({ sessionId });
    } catch (error) {
      // One bad handler must not stop the others, and must not become the caller's error.
      console.error("Reauthentication handler failed", error);
    }
  });
};

/** Test seam. Production code registers for the life of the page. */
export const clearReauthenticationHandlersForTests = () => {
  handlers.clear();
  reporting = false;
};
